import { EmbeddingBootstrapInfo, ExecuteParameters, NotificationId, VerbId } from '@tableau/api-internal-contract-js';
import * as CryptoJS from 'crypto-js';
import * as guid from 'guid';
import { ApiContext } from '../ApiContext';
import {
  defaultErrorCode,
  FunctionCallRuntimeData,
  functionComplete,
  FunctionResults,
  getBrowser,
  InternalInitializationFields,
  reportEvent,
  RunFields,
  TimingFields,
} from './TelemetryDispatcher';

const instanceIdToRunId = new Map();
const EMBEDDING_INSTANCE_ID = 'EMBEDDING-INST-ID';

type InitializationFields = EmbeddingInitializationFields & InternalInitializationFields & TimingFields & RunFields;

/**
 * Embedding specific telemetry data
 */
interface EmbeddingInitializationFields {
  curr_sheet?: string;
  is_authoring?: boolean;
  api_version?: string;
}

export function telemetryFunctionComplete(
  verb: VerbId,
  timingData: FunctionCallRuntimeData,
  parameters: ExecuteParameters,
  results: FunctionResults,
): void {
  try {
    if (!instanceIdToRunId.get(EMBEDDING_INSTANCE_ID)) {
      // we haven't initialized, so there's no runid atm.
      return;
    }

    const timingFields = timingData.getRuntimeCalculations();
    // function calls
    functionComplete(verb, timingFields, parameters, results, instanceIdToRunId.get(EMBEDDING_INSTANCE_ID));
  } catch {
    // Ignore any telemetry error
  }
}

export function telemetryNotificationComplete(
  notificationID: NotificationId,
  timingData: FunctionCallRuntimeData,
  results: FunctionResults,
): void {
  // only record viz interactive and ask-data interactive event notifications
  switch (notificationID) {
    case NotificationId.VizInteractive:
      initializeVizComplete(timingData.getRuntimeCalculations(), results);
      break;
    case NotificationId.AskDataInteractive:
      initializeAskDataComplete(timingData.getRuntimeCalculations(), results);
      break;
    default:
      break;
  }
}

function initializeAskDataComplete(timing: TimingFields, results: FunctionResults): void {
  const fields: InitializationFields = timing;

  // Create a new run-id per initialize - reload or a second initialize call will reset the run-id
  fields.run_id = guid.raw();

  //// Platform detail capture ////
  // Here we capture only browsers which have known hostnames and we actually support:
  //  chrome, firefox, edge, safari, unsupported
  fields.browser = getBrowser();

  // fields.error_code
  if (results.isErrorResult()) {
    // NOTE: We are not passing through an error code, because we don't have one at this
    //  point in time. For more fidelity in error information, we'll probably plumb through
    //  the error code from the error that was thrown on cpp side. It's a security issue
    //  to report the full error message, because it contains customer data.
    fields.error_code = defaultErrorCode;
  } else {
    const apiContext = ApiContext.getInstance();
    fields.api_version = apiContext.getExternalApiVersion();
  }

  //// Report event ////
  reportEvent('embedding_ask_data_initialized', fields).then().catch();
}

function initializeVizComplete(timing: TimingFields, results: FunctionResults): void {
  const fields: InitializationFields = timing;

  // Create a new run-id per initialize - reload or a second initialize call will reset the run-id
  fields.run_id = guid.raw();

  //// Platform detail capture ////
  // Here we capture only browsers which have known hostnames and we actually support:
  //  chrome, firefox, edge, safari, unsupported
  fields.browser = getBrowser();

  if (results.isSuccessResult()) {
    const embeddingBootstrapInfo = results.model as EmbeddingBootstrapInfo;
    fields.curr_sheet = CryptoJS.SHA256(embeddingBootstrapInfo.currWorksheetName).toString(CryptoJS.enc.Base64);

    const apiContext = ApiContext.getInstance();
    fields.is_authoring = apiContext.isAuthoring();
    fields.api_version = apiContext.getExternalApiVersion();
  }

  // fields.error_code
  if (results.isErrorResult()) {
    // NOTE: We are not passing through an error code, because we don't have one at this
    //  point in time. For more fidelity in error information, we'll probably plumb through
    //  the error code from the error that was thrown on cpp side. It's a security issue
    //  to report the full error message, because it contains customer data.
    fields.error_code = defaultErrorCode;
  }

  //// Report event ////
  reportEvent('EmbeddingAPIInitialized', fields).then().catch();

  instanceIdToRunId.set(EMBEDDING_INSTANCE_ID, fields.run_id);
}
